import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useParams } from 'react-router-dom'
import { Download, FileText, Lock, Clock } from 'lucide-react'
import { Button } from 'components/ui/button'

interface ShareInfo {
	token: string
	filename: string
	byteSize: number | null
	mimeType: string | null
	expiresAt: string
	senderName: string | null
}

type LoadState =
	| { phase: 'loading' }
	| { phase: 'ready'; info: ShareInfo }
	| { phase: 'expired'; message: string }
	| { phase: 'notFound'; message: string }
	| { phase: 'error'; message: string }

const formatBytes = (bytes: number | null): string => {
	if (bytes == null) return ''
	if (bytes < 1024) return `${bytes} B`
	if (bytes < 1024 ** 2) return `${(bytes / 1024).toFixed(1)} KB`
	if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`
	return `${(bytes / 1024 ** 3).toFixed(2)} GB`
}

const formatExpiry = (iso: string): string => {
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return ''
	return d.toLocaleString(undefined, {
		dateStyle: 'medium',
		timeStyle: 'short',
	})
}

const DocumentSharePage: React.FC = () => {
	const { token } = useParams<{ token: string }>()
	const [state, setState] = useState<LoadState>({ phase: 'loading' })

	useEffect(() => {
		if (!token) {
			setState({ phase: 'notFound', message: 'Missing share token.' })
			return
		}
		let cancelled = false
		const load = async () => {
			try {
				const { data } = await axios.get<ShareInfo>(
					`/share/document/${token}/info`,
				)
				if (!cancelled) setState({ phase: 'ready', info: data })
			} catch (err: any) {
				if (cancelled) return
				const status = err?.response?.status
				const message =
					err?.response?.data?.message ||
					'Something went wrong while loading this link.'
				if (status === 410) setState({ phase: 'expired', message })
				else if (status === 404) setState({ phase: 'notFound', message })
				else setState({ phase: 'error', message })
			}
		}
		load()
		return () => {
			cancelled = true
		}
	}, [token])

	const [downloading, setDownloading] = useState(false)
	const [downloadError, setDownloadError] = useState<string | null>(null)

	const handleDownload = async () => {
		if (!token || downloading) return
		// axios → /share/document/:token; backend 302's to a presigned R2
		// URL. axios follows the redirect cross-origin, R2 strips Auth,
		// signature on the URL is the auth. We download as a Blob and
		// trigger the save dialog with the original filename. Same trick
		// as `downloadAndSave` in libraryDocumentService.
		setDownloading(true)
		setDownloadError(null)
		try {
			const filename =
				state.phase === 'ready' ? state.info.filename : 'download'
			const { data } = await axios.get<Blob>(`/share/document/${token}`, {
				responseType: 'blob',
			})
			const objectUrl = URL.createObjectURL(data)
			try {
				const a = document.createElement('a')
				a.href = objectUrl
				a.download = filename
				document.body.appendChild(a)
				a.click()
				a.remove()
			} finally {
				setTimeout(() => URL.revokeObjectURL(objectUrl), 1000)
			}
		} catch (err: any) {
			const status = err?.response?.status
			setDownloadError(
				status === 410
					? 'This link expired while you were viewing it.'
					: 'Download failed. Try again, or ask the sender for a fresh link.',
			)
		} finally {
			setDownloading(false)
		}
	}

	return (
		<div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
			<div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-gray-200 p-8">
				<div className="flex justify-center mb-6">
					<div className="rounded-full bg-[#0080fe]/10 p-3">
						<FileText className="w-8 h-8 text-[#0080fe]" />
					</div>
				</div>

				{state.phase === 'loading' && (
					<div className="text-center text-gray-500 text-sm py-6">
						Loading shared document…
					</div>
				)}

				{state.phase === 'ready' && (
					<>
						<h1 className="text-xl font-semibold text-gray-900 text-center break-words">
							{state.info.filename}
						</h1>
						<div className="mt-4 space-y-2 text-sm text-gray-600 text-center">
							{state.info.senderName && (
								<div>Shared by <span className="font-medium text-gray-900">{state.info.senderName}</span></div>
							)}
							{state.info.byteSize != null && (
								<div className="text-gray-500">{formatBytes(state.info.byteSize)}</div>
							)}
							<div className="flex items-center justify-center gap-1.5 text-gray-500">
								<Clock className="w-3.5 h-3.5" />
								<span>Expires {formatExpiry(state.info.expiresAt)}</span>
							</div>
						</div>
						<div className="mt-6 flex flex-col items-stretch gap-2">
							<Button
								onClick={handleDownload}
								disabled={downloading}
								className="gap-2 w-full justify-center"
							>
								<Download className="w-4 h-4" />
								{downloading ? 'Downloading…' : 'Download file'}
							</Button>
							{downloadError && (
								<p className="text-xs text-red-600 text-center">{downloadError}</p>
							)}
						</div>
					</>
				)}

				{state.phase === 'expired' && (
					<div className="text-center">
						<div className="inline-flex items-center gap-1.5 text-amber-600 font-medium mb-2">
							<Lock className="w-4 h-4" />
							Link unavailable
						</div>
						<p className="text-sm text-gray-600">{state.message}</p>
						<p className="text-xs text-gray-400 mt-3">
							Ask the sender to share a fresh link.
						</p>
					</div>
				)}

				{state.phase === 'notFound' && (
					<div className="text-center">
						<div className="inline-flex items-center gap-1.5 text-gray-700 font-medium mb-2">
							<Lock className="w-4 h-4" />
							Link not found
						</div>
						<p className="text-sm text-gray-600">{state.message}</p>
					</div>
				)}

				{state.phase === 'error' && (
					<div className="text-center">
						<div className="text-red-600 font-medium mb-2">Couldn't load link</div>
						<p className="text-sm text-gray-600">{state.message}</p>
					</div>
				)}
			</div>
		</div>
	)
}

export default DocumentSharePage
